import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { useHistory } from 'react-router-dom';
import { Expand } from '@sparefoot/react-core';
import { formatUSD } from '@sparefoot/react-core/utils/format';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';

import { Paragraph, Text } from 'components/core/Typography';
import { Tooltip } from 'components/core/Tooltip';
import QuestionMark from 'components/svg/QuestionMark';
import { Button } from 'components/core/Button';
import { UnitReservationForm } from 'components/unit/UnitReservationForm';
import { FeeWarningToolTipV2 } from 'components/unit/FeeWarningToolTipV2';
import { UnitIconV2 } from 'components/unit/UnitIconV2';
import { ExpressBanner } from 'components/core/ExpressBanner';
import { formatUnitSize } from 'utils/unitNameTrim';

import './BookableUnitDetailsV2.scss';
import { TrackingClient } from '@sparefoot/tracking-client';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

export const enhance = compose(
	withState('instance', 'setRef'),
	withHandlers({
		handleExpandUnit: (props) => () => {
			const { unit, facility, isExpanded } = props;
			if (!isExpanded) {
				trackCTAClick(
					'render-reservation-form-button',
					'Continue Button',
					'Continue',
					props.url,
				);

				TrackingClient.trackFacilityUnitSelected({
					facility: {
						id: facility?.id || 'Unknown Facility ID',
						name: facility?.name || 'Unknown Facility',
						location: `${facility?.location?.city || 'Unknown City'}, ${facility?.location?.state || 'Unknown State'}`,
					},
					last_unit_added_date: new Date(),
					unit: {
						currency: 'usd',
						id: unit.id,
						variant_size: unit?.sizeText || 'Unknown Size',
						price: unit?.price?.sparefoot || 'Unknown Price',
						quantity: 1,
					},
					unit_id: unit.id,
					unit_promo: unit.promotion || 'No Promo',
				});
			}
			return props.onExpandUnit(props.unit.id, props.storageType);
		},
	}),
	lifecycle({
		componentDidUpdate(prevProps) {
			if (this.props.isExpanded && !prevProps.isExpanded) {
				// scroll after expand animation
				setTimeout(
					() =>
						scrollToElement(
							this.props.instance,
							{ offset: -84 }, // sticky search height
						),
					350,
				);
			}
		},
	}),
);

export function BookableUnitDetailsV2({
	facility,
	isExpanded,
	onCreateBooking,
	unit,
	setRef,
	isNewSearchPage,
}) {
	const history = useHistory();
	const [showAmenities, setShowAmenities] = useState(false);
	const showOMI = facility.hasOnlineMoveins;
	const regularPrice = unit?.price?.regular;
	const salePrice = unit?.price?.meta;
	const showStrikethrough =
		isNewSearchPage && regularPrice && regularPrice > salePrice;

	return (
		<div className="bookable-unit-details-v2">
			<div
				className="unit-details"
				ref={setRef}
			>
				{!!unit.tierName && (
					<div className="tier-name">
						<Paragraph>{unit.tierName}</Paragraph>
						{unit.tierDescription && (
							<Tooltip
								content={
									<span style={{ color: '#000000' }}>
										{unit.tierDescription}
									</span>
								}
								size="medium"
								backgroundColor="white"
								className="tier-tooltip-no-border"
							>
								<span className="question-mark">
									<QuestionMark showTitle={false} />
								</span>
							</Tooltip>
						)}
					</div>
				)}
				<div className="unit-overview-container">
					<div className="unit-image-and-description">
						{/* Unit Image */}
						<div className="facility-unit-image">
							<UnitIconV2
								squareFootage={unit.squareFootage}
								type={unit.unitType}
								amenities={unit.amenities}
								alt={`Depiction of a ${unit.size} self-storage unit`}
							/>
						</div>
						<div className="facility-details-container">
							{!!unit.special && (
								<div className="unit-promotion-container">
									<Paragraph
										last
										size="tiny"
										className="unit-promotion-text"
										color="greenHero"
									>
										{unit.special}
									</Paragraph>
								</div>
							)}
							<div className="facility-unit-size">
								<Text
									last
									className="unit-size"
								>
									{formatUnitSize(unit.sizeText)}{' '}
									{unit?.typeText}
								</Text>

								{/* Unit Price Per Sqft */}
								{!!unit?.pricePerSqFt && (
									<Text className="price-per-sqft-content">
										$
										{unit?.pricePerSqFt.sparefoot ||
											unit?.pricePerSqFt.regular}{' '}
										/ sqft
									</Text>
								)}
							</div>
							<div className="amenities-list">
								{!isEmpty(unit?.availableAmenities) && (
									<ul className="amenities">
										{unit?.availableAmenities
											?.slice(0, 5)
											?.map((amenity, index, array) => (
												<React.Fragment key={amenity}>
													<li
														key={amenity}
														className="amenities-item"
													>
														{amenity}
													</li>

													{index <
														array.length - 1 && (
														<span className="amenities-elipses-dot"></span>
													)}
												</React.Fragment>
											))}
										{unit?.availableAmenities?.length >
											5 && (
											<React.Fragment>
												<span className="amenities-elipses-dot"></span>
												<button
													type="button"
													className="more-amenities-btn"
													onClick={() =>
														setShowAmenities(
															!showAmenities,
														)
													}
												>
													+{' '}
													{unit?.availableAmenities
														.length - 5}{' '}
													Amenities
												</button>
											</React.Fragment>
										)}
									</ul>
								)}
								{showAmenities && (
									<ul className="amenities-remaining-list">
										{unit?.availableAmenities
											?.slice(5)
											?.map((amenity, index, array) => (
												<React.Fragment key={amenity}>
													<li
														key={amenity}
														className="amenities-remaining-list-item"
													>
														{amenity}
													</li>
													{index <
														array.length - 1 && (
														<span className="amenities-elipses-gray-dot"></span>
													)}
												</React.Fragment>
											))}
									</ul>
								)}
							</div>
						</div>
					</div>

					<div className="facility-price-reserve-container">
						<div className="facility-unit-price">
							<Fragment>
								{showStrikethrough && (
									<Text
										color="placeholderGray"
										size="small"
										className="unit-price-strikethrough"
										strikethrough
										last
									>
										{formatUSD(regularPrice)}
									</Text>
								)}
								<Text
									last
									color="red"
									size="mediumLarge"
									className="unit-price"
								>
									{formatUSD(salePrice)}
								</Text>
								{facility?.showFeeWarning &&
									facility?.totalFees > 0 && (
										<FeeWarningToolTipV2
											facility={facility}
											className="fee-tooltip-v2"
										/>
									)}
								{unit.quantity && unit.quantity <= 3 && (
									<Text
										last
										className="unit-low-quantity"
									>
										ONLY {unit?.quantity} LEFT
									</Text>
								)}
							</Fragment>
						</div>
						<div className="facility-unit-reserve-container">
							<div className="facility-unit-reserve">
								<Button
									className={classnames('reserve-button', {
										gray: isExpanded,
									})}
									onClick={() => {
										const state =
											facility?.location?.state?.toLowerCase() ||
											'';
										const city =
											facility?.location?.city
												?.toLowerCase()
												.replace(/\s+/g, '-') || '';
										const facilityName =
											facility?.name
												?.toLowerCase()
												.replace(/\s+/g, '-') || '';
										const facilityId = facility?.id || '';
										const unitId = unit?.id || '';

										const reserveUrl = `/self-storage/${state}/${city}/${facilityName}-${facilityId}/reserve/${unitId}`;
										history.push(reserveUrl);
									}}
								>
									Reserve Now, Pay Later
								</Button>
								<span className="reserve-button-bottom-border"></span>
							</div>
							<span className="reserve-information">
								You will not be charged at this time
							</span>
						</div>
					</div>
				</div>
			</div>
			<Expand showing={isExpanded}>
				<div className="reservation-form-wrapper">
					<div className="reservation-form-container">
						{showOMI ? (
							<div className="express-banner">
								<ExpressBanner last />
							</div>
						) : (
							<Paragraph className="lock-in">
								Lock in this rate now. No credit card, no
								obligation.
							</Paragraph>
						)}
						<UnitReservationForm
							onSubmit={onCreateBooking}
							unit={unit}
							facility={facility}
						/>
					</div>
				</div>
			</Expand>
		</div>
	);
}

BookableUnitDetailsV2.propTypes = {
	setRef: PropTypes.func,
	facility: PropTypes.object.isRequired,
	handleExpandUnit: PropTypes.func.isRequired,
	isExpanded: PropTypes.bool.isRequired,
	onCreateBooking: PropTypes.func.isRequired,
	unit: PropTypes.object.isRequired,
	isNewSearchPage: PropTypes.bool,
	pricePerSqft: PropTypes.string,
};

export default enhance(BookableUnitDetailsV2);
